import { useEffect, useLayoutEffect, useRef, type TextareaHTMLAttributes } from 'react';

/**
 * A textarea that is always as tall as its text, so nothing scrolls inside a box: the height follows every
 * keystroke, a change of width (the text re-wraps) and the fonts arriving. The `rows` attribute is only the
 * floor; the stylesheet keeps the box from being resized by hand, which the next keystroke would undo.
 */
export function GrowingTextarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const width = useRef(0);
  const fit = () => {
    const el = ref.current;
    if (!el) return;
    const borders = el.offsetHeight - el.clientHeight; // the height style includes them (border-box)
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight + borders}px`;
    width.current = el.clientWidth;
  };
  useLayoutEffect(fit, [props.value]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWidth = () => {
      if (el.clientWidth !== width.current) fit();
    };
    const ro = new ResizeObserver(onWidth);
    ro.observe(el);
    window.addEventListener('resize', onWidth);
    void document.fonts?.ready.then(fit);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onWidth);
    };
  }, []);
  return <textarea ref={ref} className={`grow${className ? ` ${className}` : ''}`} {...props} />;
}
